<script setup lang="ts">
import { computed } from 'vue'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Message from 'primevue/message'
import Password from 'primevue/password'
import Select from 'primevue/select'
import { ASSIGNABLE_ROLES, type UserPayload } from '@/services/users'
import { roleLabel } from '@/utils/roles'

/**
 * Formulario de alta y edición de un usuario.
 *
 * Es el mismo componente para las dos operaciones porque los campos son los
 * mismos y la única diferencia real es la contraseña: obligatoria al crear y
 * opcional al editar. Dos formularios separados divergirían en cuanto un campo
 * cambiara en uno y no en el otro.
 *
 * La contraseña viaja vacía al editar solo si el usuario la toca: si se mandara
 * siempre, cualquier guardado de un nombre borraría la contraseña de alguien.
 */

const props = defineProps<{
  initial?: {
    username: string
    name: string
    email: string | null
    phone: string | null
    active: boolean
    roles: string[]
  } | null
  submitting: boolean
  serverErrors: Record<string, string[]>
  isEdit: boolean
}>()

const emit = defineEmits<{
  submit: [payload: UserPayload]
  cancel: []
}>()

const model = defineModel<{
  username: string
  name: string
  email: string
  phone: string
  attention_line: string
  active: boolean
  roles: string[]
  password: string
  password_confirmation: string
}>({ required: true })

const roleOptions = computed(() =>
  ASSIGNABLE_ROLES.map((role) => ({
    value: role,
    label: roleLabel(role),
    description:
      role === 'admin'
        ? 'Acceso completo, incluido el panel de ventas y la gestión de usuarios.'
        : role === 'marketing'
          ? 'Solo el panel de marketing y sus descargas. No cotiza ni gestiona clientes.'
          : 'Cotiza clientes y gestiona seguimientos, sin panel ni usuarios.',
  })),
)

const selectedRole = computed({
  get: () => model.value.roles[0] ?? '',
  set: (value: string) => {
    // Un solo rol por usuario. El formulario lo expone como selector y no como
    // lista de casillas porque el backend asigna exactamente uno: ofrecer
    // several y descartar el resto sería mentir sobre lo que hace el botón.
    model.value.roles = value === '' ? [] : [value]
  },
})

function errorFor(field: string): string | null {
  return props.serverErrors[field]?.[0] ?? null
}

function submit(): void {
  const payload: UserPayload = {
    username: model.value.username,
    name: model.value.name,
    email: model.value.email === '' ? null : model.value.email,
    phone: model.value.phone === '' ? null : model.value.phone,
    attention_line: model.value.attention_line === '' ? null : model.value.attention_line,
    active: model.value.active,
    roles: model.value.roles,
  }

  // Solo se manda la contraseña si se ha escrito algo. Mandarla vacía en una
  // edición borraría el acceso de la persona que se está editando.
  if (model.value.password !== '') {
    payload.password = model.value.password
    payload.password_confirmation = model.value.password_confirmation
  }

  emit('submit', payload)
}
</script>

<template>
  <form
    class="space-y-4"
    novalidate
    @submit.prevent="submit"
  >
    <Message
      v-if="serverErrors.general"
      severity="error"
      :closable="false"
      variant="simple"
      role="alert"
    >
      {{ serverErrors.general }}
    </Message>

    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div class="space-y-1">
        <label
          for="user-username"
          class="block text-sm font-medium text-surface-700 dark:text-surface-300"
        >
          Usuario
        </label>
        <InputText
          id="user-username"
          v-model="model.username"
          autocomplete="off"
          :invalid="errorFor('username') !== null"
          class="w-full"
        />
        <p
          v-if="errorFor('username')"
          class="text-sm text-red-600 dark:text-red-400"
        >
          {{ errorFor('username') }}
        </p>
        <p
          v-else
          class="text-xs text-surface-500"
        >
          Sin espacios ni acentos: es con lo que se inicia sesión.
        </p>
      </div>

      <div class="space-y-1">
        <label
          for="user-name"
          class="block text-sm font-medium text-surface-700 dark:text-surface-300"
        >
          Nombre
        </label>
        <InputText
          id="user-name"
          v-model="model.name"
          autocomplete="off"
          :invalid="errorFor('name') !== null"
          class="w-full"
        />
        <p
          v-if="errorFor('name')"
          class="text-sm text-red-600 dark:text-red-400"
        >
          {{ errorFor('name') }}
        </p>
      </div>

      <div class="space-y-1">
        <label
          for="user-email"
          class="block text-sm font-medium text-surface-700 dark:text-surface-300"
        >
          Email
        </label>
        <InputText
          id="user-email"
          v-model="model.email"
          type="email"
          autocomplete="off"
          :invalid="errorFor('email') !== null"
          class="w-full"
        />
        <p
          v-if="errorFor('email')"
          class="text-sm text-red-600 dark:text-red-400"
        >
          {{ errorFor('email') }}
        </p>
      </div>

      <div class="space-y-1">
        <label
          for="user-phone"
          class="block text-sm font-medium text-surface-700 dark:text-surface-300"
        >
          Teléfono
        </label>
        <InputText
          id="user-phone"
          v-model="model.phone"
          type="tel"
          autocomplete="off"
          :invalid="errorFor('phone') !== null"
          class="w-full"
        />
        <p
          v-if="errorFor('phone')"
          class="text-sm text-red-600 dark:text-red-400"
        >
          {{ errorFor('phone') }}
        </p>
      </div>

      <!--
        Línea de atención. Se usa para segmentar las cotizaciones que crea este
        asesor y aparece en filtros, listados, PDF y Excel.
      -->
      <div class="space-y-1">
        <label
          for="user-attention-line"
          class="block text-sm font-medium text-surface-700 dark:text-surface-300"
        >
          Línea de atención
        </label>
        <InputText
          id="user-attention-line"
          v-model="model.attention_line"
          autocomplete="off"
          placeholder="Ej: Retail, Mayorista, Gobierno..."
          class="w-full"
        />
      </div>

      <div class="space-y-1">
        <label
          for="user-role"
          class="block text-sm font-medium text-surface-700 dark:text-surface-300"
        >
          Rol
        </label>
        <Select
          v-model="selectedRole"
          input-id="user-role"
          :options="roleOptions"
          option-label="label"
          option-value="value"
          placeholder="Selecciona un rol"
          class="w-full"
        />
        <p
          v-if="selectedRole !== ''"
          class="text-xs text-surface-500"
        >
          {{ roleOptions.find((option) => option.value === selectedRole)?.description }}
        </p>
      </div>

      <div class="space-y-1">
        <label
          for="user-active"
          class="block text-sm font-medium text-surface-700 dark:text-surface-300"
        >
          Estado
        </label>
        <div class="flex items-center gap-2 pt-2">
          <input
            id="user-active"
            v-model="model.active"
            type="checkbox"
            class="h-4 w-4"
          >
          <label
            for="user-active"
            class="text-sm text-surface-600 dark:text-surface-300"
          >
            Activo
          </label>
        </div>
        <p class="text-xs text-surface-500">
          Un usuario desactivado conserva sus cotizaciones, pero no puede entrar.
        </p>
      </div>
    </div>

    <fieldset class="space-y-1">
      <legend class="mb-2 text-sm font-medium text-surface-700 dark:text-surface-300">
        Contraseña
      </legend>

      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div class="space-y-1">
          <label
            for="user-password"
            class="block text-sm font-medium text-surface-700 dark:text-surface-300"
          >
            Contraseña
          </label>
          <Password
            v-model="model.password"
            input-id="user-password"
            :feedback="false"
            toggle-mask
            autocomplete="new-password"
            :invalid="errorFor('password') !== null"
            class="w-full"
          />
        </div>

        <div class="space-y-1">
          <label
            for="user-password-confirm"
            class="block text-sm font-medium text-surface-700 dark:text-surface-300"
          >
            Repetir contraseña
          </label>
          <Password
            v-model="model.password_confirmation"
            input-id="user-password-confirm"
            :feedback="false"
            toggle-mask
            autocomplete="new-password"
            :invalid="errorFor('password_confirmation') !== null"
            class="w-full"
          />
        </div>
      </div>

      <p
        v-if="errorFor('password')"
        class="text-sm text-red-600 dark:text-red-400"
      >
        {{ errorFor('password') }}
      </p>
      <p
        v-else
        class="text-xs text-surface-500"
      >
        <template v-if="isEdit">
          Déjala vacía para no cambiarla. Al cambiarla se cierran las sesiones
          abiertas de esa persona.
        </template>
        <template v-else>
          Mínimo 8 caracteres.
        </template>
      </p>
    </fieldset>

    <div class="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
      <Button
        label="Cancelar"
        severity="secondary"
        text
        type="button"
        :disabled="submitting"
        @click="emit('cancel')"
      />
      <Button
        :label="isEdit ? 'Guardar cambios' : 'Crear usuario'"
        icon="pi pi-check"
        type="submit"
        :loading="submitting"
      />
    </div>
  </form>
</template>
